import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link as MuiLink
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { apiClient, setCsrfToken } from '@shared/services/ApiClient'
import PasswordStrengthMeter from '../components/PasswordStrengthMeter'

interface InvitationInfo {
  email: string
  firstName: string
  lastName: string
  role: string
}

interface AcceptInvitationForm {
  password: string
  confirmPassword: string
  phoneNumber?: string
  timeZone: string
}

const schema = yup.object({
  password: yup
    .string()
    .required('Password is required')
    .min(15, 'Password must be at least 15 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  phoneNumber: yup.string(),
  timeZone: yup.string().required('Time zone is required')
})

function AcceptInvitationPage() {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [loadingInfo, setLoadingInfo] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null)
  const token = searchParams.get('token')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<AcceptInvitationForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    }
  })

  const password = watch('password', '')

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please check your email and try again.')
      setLoadingInfo(false)
    } else {
      fetchInvitationInfo()
    }
  }, [token])

  const fetchInvitationInfo = async () => {
    if (!token) return

    try {
      setLoadingInfo(true)
      const info = await apiClient.get<InvitationInfo>(`/auth/invitation-info?token=${token}`)
      setInvitationInfo(info)
    } catch (err: any) {
      console.error('Failed to load invitation info:', err)
      // Don't set error - user can still submit without seeing email
    } finally {
      setLoadingInfo(false)
    }
  }

  const onSubmit = async (data: AcceptInvitationForm) => {
    if (!token) {
      setError('Invalid invitation token')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await apiClient.post<any>('/auth/accept-invitation', {
        token,
        password: data.password,
        confirmPassword: data.confirmPassword,
        phoneNumber: data.phoneNumber || null,
        timeZone: data.timeZone
      })

      setSuccess(true)

      if (response.csrfToken) {
        setCsrfToken(response.csrfToken)
      }

      // Redirect based on role after short delay (auth uses HttpOnly cookies)
      setTimeout(() => {
        const role = response.user?.role
        if (role === 'Admin') {
          window.location.href = '/admin/dashboard'
        } else if (role === 'Agent') {
          window.location.href = '/agent/dashboard'
        } else {
          // Customer or Oversight
          window.location.href = '/customer/dashboard'
        }
      }, 1500)
    } catch (err: any) {
      console.error('Accept invitation error:', err)
      setError(err.message || 'Failed to accept invitation. The link may have expired.')
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50'
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', mx: 2 }}>
          <Alert severity="error">
            Invalid invitation link. Please check your email and try again.
          </Alert>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <MuiLink href="/login" sx={{ cursor: 'pointer' }}>
              Go to Login
            </MuiLink>
          </Box>
        </Paper>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        py: 4
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', mx: 2 }}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Accept Invitation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set your password to activate your account
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Account activated successfully! Redirecting...
          </Alert>
        )}

        {loadingInfo ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {invitationInfo?.email && (
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={invitationInfo.email}
                disabled
                autoComplete="username email"
                sx={{ mb: 2 }}
                helperText="Your account email address"
              />
            )}

            <TextField
              fullWidth
              type="password"
              label="Password"
              autoComplete="new-password"
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
              disabled={loading || success}
              sx={{ mb: 2 }}
            />
            <PasswordStrengthMeter password={password || ''} />

          <TextField
            fullWidth
            type="password"
            label="Confirm Password"
            autoComplete="new-password"
            {...register('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            disabled={loading || success}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Phone Number (Optional)"
            placeholder="+44 20 1234 5678"
            {...register('phoneNumber')}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber?.message}
            disabled={loading || success}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Time Zone"
            {...register('timeZone')}
            error={!!errors.timeZone}
            helperText={errors.timeZone?.message || 'Detected automatically'}
            disabled={loading || success}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || success}
            sx={{ mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Activate Account'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <MuiLink href="/login" sx={{ cursor: 'pointer' }}>
                Sign In
              </MuiLink>
            </Typography>
          </Box>
        </form>
        )}
      </Paper>
    </Box>
  )
}

export default AcceptInvitationPage
